const DISPLAY_ACRONYMS = new Set([
	'ip',
	'isp',
	'vpn',
	'tor',
	'dns',
	'url',
	'uri',
	'mac',
	'asn',
	'os',
	'sid',
	'pid',
	'uuid',
	'id',
	'api',
	'cidr',
	'fqdn',
	'mfa',
	'sso',
	'otp',
	'jwt',
	'iam',
	// `IT_OPS` — the action category. Without this the active-filter badge
	// humanises it to "It Ops" while the cell renders "IT Ops". A field whose
	// whole word is literally "it" would now render "IT"; none exists.
	'it',
	'ioc',
	'ttp',
	'cve',
	'cvss',
	'nat',
	'tls',
	'ssl',
	'http',
	'https',
	'json',
	'xml',
	'csv',
	'io',
])

const DISPLAY_OVERRIDES: Record<string, string> = {
	abuseipdb: 'AbuseIPDB',
	ipgeolocation_io: 'IPGeolocation.io',
	bitlyft_essential: 'BitLyft Essential',
}

/**
 * Convert a backend key/token to a display-ready label with acronym
 * preservation. **Display-only** — the output is not round-trippable back to
 * the wire value (`bitlyft_essential` → "BitLyft Essential" → nothing). A
 * filter dropdown must therefore keep the raw value and carry the label
 * alongside it (`FilterOptionValue`'s object form), never reconstruct one from
 * the other.
 *
 * @param name The string to convert
 * @returns The display name
 *
 * @example
 * toDisplayName('source_ip') // 'Source IP'
 * toDisplayName('isp') // 'ISP'
 * toDisplayName('abuseipdb') // 'AbuseIPDB'
 * toDisplayName('ipgeolocation_io') // 'IPGeolocation.io'
 */
export const toDisplayName = (name: string): string => {
	if (!name) return name
	const lower = name.toLowerCase()
	if (DISPLAY_OVERRIDES[lower]) return DISPLAY_OVERRIDES[lower]
	return name
		.split('_')
		.map((word) => {
			if (word === '') return word
			const lw = word.toLowerCase()
			if (DISPLAY_ACRONYMS.has(lw)) return lw.toUpperCase()
			return lw[0].toUpperCase() + lw.slice(1)
		})
		.join(' ')
}

/**
 * Strip leading whitespace from each line outside fenced code blocks.
 *
 * AI text deltas sometimes arrive with multiple leading spaces (4+) which
 * Markdown interprets as an indented code block, producing a monospace box.
 * Fenced blocks (``` or ~~~) are preserved verbatim so legitimate code stays
 * formatted.
 *
 * @param input - Raw text from the model.
 * @returns Text with leading whitespace stripped from prose lines.
 */
export function stripLeadingIndent(input: string): string {
	if (!input) return input

	// Construct the stateful (`g`-flag) regex per call. A module-level instance
	// would share `lastIndex` across concurrent React renders and corrupt
	// mid-iteration state.
	const fencePattern =
		/(^|\n)([ \t]*)(```|~~~)[^\n]*\n[\s\S]*?(?:\n\2\3[^\n]*(?=\n|$)|$)/g

	const segments: { text: string; isFence: boolean }[] = []
	let lastIndex = 0

	let match: RegExpExecArray | null
	while ((match = fencePattern.exec(input)) !== null) {
		const leading = match[1] ?? ''
		const fenceStart = match.index + leading.length
		if (fenceStart > lastIndex) {
			segments.push({
				text: input.slice(lastIndex, fenceStart),
				isFence: false,
			})
		}
		const fenceEnd = match.index + match[0].length
		segments.push({ text: input.slice(fenceStart, fenceEnd), isFence: true })
		lastIndex = fenceEnd
	}
	if (lastIndex < input.length) {
		segments.push({ text: input.slice(lastIndex), isFence: false })
	}

	return segments
		.map((s) => (s.isFence ? s.text : s.text.replace(/^[ \t]+/gm, '')))
		.join('')
}
